import { useRef, useEffect } from "react";
import { cn } from "@/lib/utils";

interface ScaledPreviewProps {
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

// Helper function to scale border radius
const scaleBorderRadius = (radius: number | string | undefined) => {
    if (!radius) return undefined;
    const value = typeof radius === "number" ? radius : parseFloat(radius);
    // Scale the border radius to be proportional to the container size
    // Using a smaller base value for more precise control
    return `${value * 0.5}em`;
};

export const ScaledPreview = ({ children, className, style }: ScaledPreviewProps) => {
    const previewRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateFontSize = () => {
            if (previewRef.current) {
                const width = previewRef.current.clientWidth;
                // Set font-size so 1em = width / 16 (for 16:9 aspect ratio)
                // This creates a coordinate system where the width is 16em
                // and the height is 9em (for a 16:9 aspect ratio)
                previewRef.current.style.fontSize = `${width / 16}px`;
            }
        };

        // Initial update
        updateFontSize();

        // Add resize listener with debounce
        let timeout: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeout);
            timeout = setTimeout(updateFontSize, 100);
        };

        window.addEventListener("resize", handleResize);

        // Cleanup
        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(timeout);
        };
    }, []);

    // Scale any border radius in the style prop
    const scaledStyle = style
        ? {
              ...style,
              borderRadius: style.borderRadius ? scaleBorderRadius(style.borderRadius) : undefined,
          }
        : undefined;

    return (
        <div
            ref={previewRef}
            className={cn(
                "relative w-full aspect-video bg-background border border-border rounded-lg shadow-sm overflow-hidden",
                className,
            )}
            style={scaledStyle}
        >
            {children}
        </div>
    );
};
